<template>
  <div class="mt-3">
    <div class="flex items-center justify-between mb-2">
      <h4 class="font-semibold text-sm text-ink-soft">Delivery Notes</h4>
      <span v-if="isFetching" class="text-xs text-ink-mute">Loading...</span>
    </div>

    <div v-if="error" class="text-xs text-red-600 mb-2">Failed to load delivery notes.</div>

    <div v-if="notes.length === 0 && !isFetching" class="text-xs text-ink-mute">
      No delivery notes found.
    </div>

    <div v-else class="overflow-hidden border border-line rounded-md">
      <table class="min-w-full bg-surface">
        <thead class="bg-surface-2">
          <tr>
            <th class="px-3 py-2 text-left text-xs font-medium text-ink-soft">Label</th>
            <th class="px-3 py-2 text-right text-xs font-medium text-ink-soft">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(note, idx) in notesList" :key="note.id" class="border-t border-line-soft">
            <td class="px-3 py-2 text-sm text-ink-soft">
              {{ `Delivery Note (${getBoxLabel(note, idx)})` }}
            </td>
            <td class="px-3 py-2">
              <div class="flex items-center justify-end gap-2">
                <button
                  class="px-2 py-1 text-xs bg-surface-2 hover:bg-surface-3 rounded border border-line"
                  @click="onDownloadPDF(note)"
                  :disabled="isLoading"
                >
                  Download PDF
                </button>
                <button
                  v-if="can('edit-delivery-notes')"
                  class="px-2 py-1 text-xs bg-surface-2 hover:bg-surface-3 rounded border border-line"
                  @click="onEdit(note)"
                  :disabled="isLoading"
                >
                  Edit
                </button>
                <button
                  v-if="can('delete-delivery-notes')"
                  class="px-2 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-700 rounded border border-red-200"
                  @click="onDelete(note)"
                  :disabled="isLoading"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { can } from '@/composables';
import { showConfirm } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { DeliveryNote } from '@/models';
import { ref, watch } from 'vue';

interface Props {
  order_item_id: number | string;
  isFetching: boolean;
  notesError: string | null;
  notes: DeliveryNote[];
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'edit', note: DeliveryNote): void;
  (e: 'download-pdf', note: DeliveryNote): void;
  (e: 'deleted', id: number | string): void;
}>();

const notesList = ref<DeliveryNote[]>(props.notes ?? []);
const isLoading = ref(false);
const error = ref<any>(null);

watch(
  () => props.notes,
  (val) => {
    notesList.value = val ?? [];
  }
);

const getBoxLabel = (note: DeliveryNote, idx: number) => {
  if (!note?.boxes || note.boxes.length === 0) return '—';
  const box = note.boxes[0];
  if (!box) return '—';
  if (box.box_id) {
    return `${box.box_label} - ${box.box_id}`;
  }
  // If no box_id, return index 1 (since it's always the first/only box)
  return `${box.box_label} - ${idx + 1}`;
};

const onEdit = (note: DeliveryNote) => {
  emit('edit', note);
};
const onDownloadPDF = (note: DeliveryNote) => {
  emit('download-pdf', note);
};

const onDelete = async (note: DeliveryNote) => {
  if (!note?.id) return;
  const confirmed = await showConfirm(
    'Delete Note',
    'Are you sure you want to delete this delivery note?'
  );
  if (!confirmed) return;
  isLoading.value = true;
  try {
    await http.delete(`/delivery-note/${note.id}`);
    emit('deleted', note.id);
  } catch (err) {
    error.value = err;
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped></style>
